// src/components/admin/pages/Logs.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, RefreshCw, Eye, ExternalLink, AlertTriangle, Clock } from 'lucide-react';
import { logsApi } from '../../../services/adminApi';

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    module: 'all',
    timeRange: 'today'
  });
  const [filteredLogs, setFilteredLogs] = useState([]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await logsApi.getLogs({ limit: 100 });
      setLogs(result.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError(err.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Apply client-side filtering since backend doesn't support it yet
  useEffect(() => {
    let filtered = [...logs];

    // Filter by action type
    if (filters.type !== 'all') {
      filtered = filtered.filter(log => log.type === filters.type);
    }

    // Filter by module
    if (filters.module !== 'all') {
      filtered = filtered.filter(log => log.module === filters.module);
    }

    // Filter by time range
    if (filters.timeRange !== 'all') {
      const now = new Date();
      let cutoffDate = new Date();

      switch (filters.timeRange) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        default:
          cutoffDate = null;
      }

      if (cutoffDate) {
        filtered = filtered.filter(log => new Date(log.timestamp) >= cutoffDate);
      }
    }

    setFilteredLogs(filtered);
  }, [logs, filters]);

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0) {
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else {
        return 'Just now';
      }
    } catch {
      return 'Unknown';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'created': return 'text-green-600 bg-green-50';
      case 'updated': return 'text-blue-600 bg-blue-50';
      case 'published': return 'text-purple-600 bg-purple-50';
      case 'deactivated': return 'text-orange-600 bg-orange-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getModuleLink = (module) => {
    switch (module) {
      case 'top_picks_pool': return '/admin/top-picks';
      case 'new_releases': return '/admin/new-releases';
      case 'book_of_week': return '/admin/book-of-week';
      default: return '/admin';
    }
  };

  const getModuleDisplayName = (module) => {
    switch (module) {
      case 'top_picks_pool': return 'Top Picks Pool';
      case 'new_releases': return 'New Releases';
      case 'book_of_week': return 'Book of the Week';
      default: return module;
    }
  };

  // Get unique modules and action types from logs for filter dropdowns
  const availableModules = [...new Set(logs.map(log => log.module))].filter(Boolean);
  const availableTypes = [...new Set(logs.map(log => log.type))].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Admin Logs</h1>
          <p className="text-text-secondary">
            View recent admin actions and system events
            {logs.length > 0 && (
              <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                {filteredLogs.length} of {logs.length} entries
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-bg-surface rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="border border-gray-200 rounded px-3 py-1 text-sm"
          >
            <option value="all">All Actions</option>
            {availableTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filters.module}
            onChange={(e) => setFilters(prev => ({ ...prev, module: e.target.value }))}
            className="border border-gray-200 rounded px-3 py-1 text-sm"
          >
            <option value="all">All Modules</option>
            {availableModules.map(module => (
              <option key={module} value={module}>{getModuleDisplayName(module)}</option>
            ))}
          </select>

          <select
            value={filters.timeRange}
            onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
            className="border border-gray-200 rounded px-3 py-1 text-sm"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Log Entries */}
      <div className="bg-bg-surface rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-text-primary flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Recent Activity</span>
            {error && (
              <div className="text-red-600 text-sm ml-2">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                {error}
              </div>
            )}
          </h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
            <p className="text-text-secondary">Loading logs...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-600 mb-4">Failed to load logs</p>
            <button
              onClick={fetchLogs}
              className="px-4 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
            >
              Try Again
            </button>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-text-secondary">
              {logs.length === 0 ? 'No activity logs available' : 'No logs match the current filters'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredLogs.map((entry) => (
              <div key={`${entry.id}-${entry.timestamp}`} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(entry.type)}`}>
                        {entry.type}
                      </span>
                      <span className="font-medium text-text-primary">{entry.description}</span>
                      <span className="text-xs text-text-secondary">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>

                    <div className="text-sm text-text-secondary space-y-1">
                      {entry.module && (
                        <p>Module: {getModuleDisplayName(entry.module)}</p>
                      )}
                      {entry.ageGroup && (
                        <p>Age Group: {entry.ageGroup}</p>
                      )}
                      {(entry.bookTitle || entry.bookAuthor) && (
                        <p>
                          Book: {entry.bookTitle}
                          {entry.bookAuthor && ` by ${entry.bookAuthor}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    {entry.module && (
                      <Link
                        to={getModuleLink(entry.module)}
                        className="p-1 text-text-secondary hover:text-text-primary"
                        title={`Go to ${getModuleDisplayName(entry.module)}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Info */}
      {filteredLogs.length > 0 && (
        <div className="text-center text-sm text-text-secondary">
          <p>
            Showing {filteredLogs.length} log entries
            {logs.length !== filteredLogs.length && ` (filtered from ${logs.length} total)`}
          </p>
          <p className="mt-1">
            Note: Logs are based on editorial record changes. Full audit logging will be available in a future version.
          </p>
        </div>
      )}
    </div>
  );
};

export default Logs;